<script lang="ts">
import GenericSlider from "@/Components/Generics/GenericSlider.vue";
import HelpButton from "@/Components/Generics/HelpButton.vue";
import Icon from "@/Components/Generics/Icon.vue";
import Plane from "@/Components/Generics/Plane.vue";
import { Qualification, QualificationValue } from "@/Types";
import { CompanyRequirement } from "@/Types/company";
import { defineComponent, PropType } from "vue";

export default defineComponent({
    components: {
        Plane,
        GenericSlider,
        HelpButton,
        Icon,
    },
    props: {
        qualification: {
            type: Object as PropType<Qualification>,
            required: true,
        },
        qualificationValue: {
            type: Object as PropType<QualificationValue | null>,
            default: null,
        },
        companyRequirement: {
            type: Object as PropType<CompanyRequirement>,
            default: null,
        },
        company: {
            type: Boolean,
            default: false,
        },
        create: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["qualificationValueChanged", "createValueChanged"],
    data: () => ({}),
    computed: {
        isPreference() {
            return this.qualification.selection_type === "PREFERENCE";
        },
        realQualificationValue() {
            return this.qualificationValue?.value ?? 0;
        },
        realCompanyRequirementValue() {
            if (!this.companyRequirement) return 0;

            return this.companyRequirement.value;
        },

        qualificationNameKey(): string {
            return `qualifications.${this.qualification.name}`;
        },
        qualificationName(): string {
            return this.$trans(this.qualificationNameKey);
        },
        qualificationValueText(): string {
            let qualificationValue = this.realQualificationValue;

            if (this.company || this.create) qualificationValue = this.realCompanyRequirementValue;

            if (this.isPreference) {
                return this.$trans(`qualifications.${this.qualification.name}_${this.company || this.create ? "" : "student_"}value_${qualificationValue}`);
            } else {
                return this.$trans(`qualifications.value_${qualificationValue}`);
            }
        },

        sliderMin() {
            if (this.isPreference) return -1;
            else return 0;
        },
        sliderMax() {
            if (this.isPreference) return 1;
            else return 5;
        },
    },
    methods: {},
});
</script>

<template>
    <div class="company-skill-entry">
        <Plane class="company-skill-entry-plane flex--width">
            <div class="company-skill-entry-plane-content flex flex--h flex--width flex--height flex--halign-sb flex--valign-c flex--gap-30" :class="{ 'company-skill-entry-plane-content--create': create }">
                <div v-if="create" class="flex flex--width flex--halign-sb">
                    <Transition name="generic-fade" mode="out-in">
                        <div :key="`${qualification.id}-${qualificationNameKey}`" class="company-skill-entry-text text text--primary-b text--size-20 text--left text--balance text--semi-bold flex flex--valign-c flex--gap-10">
                            <HelpButton :title="qualificationNameKey" :extra="'_description'" />
                            {{ qualificationName }}
                            <Icon v-if="company && companyRequirement?.important" class="company-skill-entry-icon" :identifier="'djf_update_star'" />
                        </div>
                    </Transition>

                    <div class="flex flex--valign-c flex--gap-15">
                        <input
                            id="checkboxImportant"
                            :checked="companyRequirement?.important === 1"
                            class="form-check-input"
                            name="important"
                            type="checkbox"
                            @input="$emit('createValueChanged', realCompanyRequirementValue, companyRequirement?.important === 1 ? 0 : 1)" />
                        <label class="form-check-label" for="checkboxImportant">
                            {{ $trans("qualifications.important") }}
                        </label>
                        <Icon class="company-skill-entry-icon" :identifier="'djf_update_star'" />
                    </div>
                </div>

                <Transition v-else name="generic-fade" mode="out-in">
                    <div :key="`${qualification.id}-${qualificationNameKey}`" class="company-skill-entry-text text text--primary-b text--size-20 text--left text--balance text--semi-bold flex flex--valign-c flex--gap-10">
                        <HelpButton :title="qualificationNameKey" :extra="'_description'" />
                        {{ qualificationName }}
                        <Icon v-if="company && companyRequirement?.important" class="company-skill-entry-icon" :identifier="'djf_update_star'" />
                    </div>
                </Transition>

                <Transition name="generic-fade" mode="out-in">
                    <div :key="qualification.id" class="qualification-slider flex--extend flex flex--v flex--valign-c">
                        <GenericSlider v-if="!create" :value="realQualificationValue" :min="sliderMin" :max="sliderMax" :disabled="company" @change="(val) => $emit('qualificationValueChanged', val)" />
                        <GenericSlider
                            v-if="company || create"
                            :class="{ 'alternate-slider': company }"
                            :value="realCompanyRequirementValue"
                            :min="sliderMin"
                            :max="sliderMax"
                            :disabled="company"
                            :alternate="company"
                            @change="(val) => $emit('createValueChanged', val, companyRequirement?.important ?? 0)" />
                    </div>
                </Transition>

                <Transition name="generic-fade" mode="out-in">
                    <div :key="`${qualification.id}-${qualificationValueText}`" class="company-skill-entry-text company-skill-entry-text--desc text text--primary-b text--size-18 text--height-22 text--right text--balance text--semi-bold">
                        {{ qualificationValueText }}
                    </div>
                </Transition>
            </div>
        </Plane>
        <!-- <div class="company-skill-entry-help-button">
            <HelpButton />
        </div> -->
    </div>
</template>

<style lang="scss" scoped>
.form-check-input {
    margin-top: rem(-1);
    width: rem(20);
    height: rem(20);
}
.company-skill-entry {
    width: 100%;

    .company-skill-entry-plane {
        min-height: rem(80);
        padding-top: 0;
        padding-bottom: 0;

        .company-skill-entry-plane-content {
            &.company-skill-entry-plane-content--create {
                flex-direction: column;
                justify-content: space-between;
                gap: 0;
                padding: rem(10) 0;

                .qualification-slider {
                    width: 67%;
                }

                .company-skill-entry-text {
                    &.company-skill-entry-text--desc {
                        width: 100%;
                        text-align: center;
                        justify-content: center;
                    }
                }
            }

            .company-skill-entry-text {
                width: 32%;
            }

            .company-skill-entry-icon {
                width: rem(32);
            }

            .qualification-slider {
                .alternate-slider {
                    margin-top: rem(-20);
                }
            }
        }
    }
    // .company-skill-entry-help-button {
    //     position: absolute;
    //     left: calc(100% - 20px);
    //     top: 50%;
    //     transform: translateY(-50%);
    //     z-index: 100;
    // }
}

@include media-breakpoint-down(xl) {
    .company-skill-entry {
        .company-skill-entry-plane {
            padding-left: rem(15);
            padding-right: rem(15);
        }
    }
}
@include media-breakpoint-down(lg) {
    .company-skill-entry {
        .company-skill-entry-plane {
            .company-skill-entry-plane-content {
                &:not(.company-skill-entry-plane-content--create) {
                    flex-direction: column;
                    gap: 0;
                    padding: rem(10) 0;

                    .company-skill-entry-text {
                        width: 100%;
                        text-align: center;
                        justify-content: center;
                    }
                }

                .qualification-slider {
                    width: calc(100% - #{rem(100)});
                }
            }
        }
    }
}
@include media-breakpoint-down(md) {
    .company-skill-entry {
        .company-skill-entry-plane {
            padding-left: rem(10);
            padding-right: rem(10);

            .company-skill-entry-plane-content {
                .qualification-slider {
                    width: calc(100% - #{rem(50)});
                }
            }
        }
    }
}
@include media-breakpoint-down(sm) {
    .company-skill-entry {
        .company-skill-entry-plane {
            padding-left: rem(5);
            padding-right: rem(5);
        }
    }
}
</style>
